import React, { useState } from "react";
import { BaseImportExportModalComponent } from "../../../components";
import { DataTypes, useSmokeballApp } from "../../../shared";
import { getRegionFromURL } from "../../../shared/utils/region";
import {
  getTimeFeesDisbursementsRegionSpecificLabel,
  PRODUCT_TIERS,
} from "../../../shared/utils/constants";
import log from "../../../shared/utils/log";
import { useNewMigration, useSubmitMigration } from "../../../hooks";
import { useNavigate } from "react-router-dom";

export interface ExportOption {
  label: string;
  dataTypes: string[];
  selected: boolean;
}

export const ExportTypeSelectionModal: React.FC<{
  closeModal: () => void;
  productId: string | undefined;
  titleText: string;
  subtitleText: string;
  exportReason?: string;
}> = ({ closeModal, productId, titleText, subtitleText, exportReason }) => {
  const navigate = useNavigate();
  const region = getRegionFromURL();
  const [dataTypesToShow, setDataTypesToShow] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { auth, context } = useSmokeballApp();

  const { handleNewMigrationClick, isLoading: isInitialising } =
    useNewMigration();
  const { handleSubmitMigrationClick, isLoading: isSubmitting } =
    useSubmitMigration({
      accountId: context?.firmId || "",
      token: auth?.token,
    });

  // Matter relationships always needs to be associated with matters but should only be shown for Grow or Prosper clients
  const isGrowOrProsper =
    productId === PRODUCT_TIERS.GROW || productId === PRODUCT_TIERS.PROSPER;

  const [options, setOptions] = useState<ExportOption[]>(() => {
    // Initialise options with region and tier logic
    let initialOptions: ExportOption[] = [
      {
        label: DataTypes.Staff,
        selected: false,
        dataTypes: [DataTypes.Staff],
      },
      {
        label: DataTypes.Contacts,
        selected: false,
        dataTypes: [DataTypes.Contacts],
      },
      {
        label: DataTypes.Matters,
        selected: false,
        dataTypes: [
          DataTypes.Matters,
          ...(isGrowOrProsper ? [DataTypes.MatterRelationships] : []),
        ],
      },
      // {
      //   label: DataTypes.OpeningBalances,
      //   selected: false,
      //   dataTypes: [DataTypes.OpeningBalances],
      // },
      {
        label: getTimeFeesDisbursementsRegionSpecificLabel(region),
        selected: false,
        dataTypes: [
          // DataTypes.ActivityTypes,
          DataTypes.UnbilledTimeAndFees,
          DataTypes.UnbilledDisbursements,
        ],
      },
      // {
      //   label: DataTypes.Invoices,
      //   selected: false,
      //   dataTypes: [DataTypes.Invoices],
      // },
    ];

    if (
      productId === PRODUCT_TIERS.BOOST ||
      productId === PRODUCT_TIERS.GROW ||
      productId === PRODUCT_TIERS.PROSPER
    ) {
      initialOptions.push(
        {
          label: DataTypes.Memos,
          selected: false,
          dataTypes: [DataTypes.Memos],
        },
        {
          label: DataTypes.Tasks,
          selected: false,
          dataTypes: [DataTypes.Tasks],
        },
      );
    }

    // // Add Safe Custody Packets and Documents only for Grow/Prosper tiers and non-US regions
    // if (
    //   (productId === PRODUCT_TIERS.GROW ||
    //     productId === PRODUCT_TIERS.PROSPER) &&
    //   region !== "US"
    // ) {
    //   initialOptions.push({
    //     label: "Safe Custody Packets and Documents",
    //     selected: false,
    //     dataTypes: [
    //       DataTypes.SafeCustodyPackets,
    //       DataTypes.SafeCustodyDocuments,
    //     ],
    //   });
    // }

    return initialOptions;
  });

  const handleOptionChange = (index: number) => {
    const newOptions = [...options];
    newOptions[index].selected = !newOptions[index].selected;

    const selectedOption = newOptions[index];

    // If the option is selected, add its cards to the list
    if (selectedOption.selected) {
      setDataTypesToShow((prevDataTypes) => {
        const newDataTypes = [...prevDataTypes];
        selectedOption.dataTypes.forEach((dataType) => {
          if (!newDataTypes.includes(dataType)) {
            newDataTypes.push(dataType); // Add the card if it's not already in the list
          }
        });
        return newDataTypes;
      });
    } else {
      // If the option is deselected, check if any other selected options require this option's export types
      setDataTypesToShow((prevDataTypes) => {
        const newDataTypes = prevDataTypes.filter(
          (dataType) =>
            !selectedOption.dataTypes.includes(dataType) ||
            // Keep export types that are still required by other selected options
            newOptions.some(
              (option) =>
                option.selected && option.dataTypes.includes(dataType),
            ),
        );
        return newDataTypes;
      });
    }

    setOptions(newOptions);
  };

  const handleUnselectAll = () => {
    setOptions(options.map((option) => ({ ...option, selected: false })));
    setDataTypesToShow([]); // Clear export types when unselecting all
  };

  const handleSelectAll = () => {
    // Always show these export types for all tiers
    const defaultDataTypes = [
      DataTypes.Staff,
      DataTypes.Contacts,
      DataTypes.Matters,
      ...(isGrowOrProsper ? [DataTypes.MatterRelationships] : []),
      // DataTypes.OpeningBalances,
      // DataTypes.ActivityTypes,
      DataTypes.UnbilledTimeAndFees,
      DataTypes.UnbilledDisbursements,
      // DataTypes.Invoices,
    ];

    dataTypesToShow.push(...defaultDataTypes);

    // Add region and tier specific exports
    if (
      productId === PRODUCT_TIERS.BOOST ||
      productId === PRODUCT_TIERS.GROW ||
      productId === PRODUCT_TIERS.PROSPER
    ) {
      dataTypesToShow.push(DataTypes.Memos, DataTypes.Tasks);
    }

    // if (isGrowOrProsper && region !== "US") {
    //   dataTypesToShow.push(
    //     DataTypes.SafeCustodyPackets,
    //     DataTypes.SafeCustodyDocuments,
    //   );
    // }

    setOptions(options.map((option) => ({ ...option, selected: true })));
    setDataTypesToShow(dataTypesToShow);
  };

  const handleProceed = async () => {
    setIsLoading(true);
    log("Export Initialising:", isInitialising);

    try {
      // Ensure selected types are unique
      const uniqueDataTypes = [...new Set(dataTypesToShow)];
      log("Final unique export types: ", uniqueDataTypes);

      // Initialise the migration, passing "smokeball-export" as the source system
      const sourceSystem = "smokeball-export";
      const migrationSetId = await handleNewMigrationClick(
        uniqueDataTypes,
        sourceSystem,
        undefined,
        exportReason,
      );

      if (!migrationSetId) {
        console.error("Failed to initialize export migration.");
        return;
      }

      log("Export Migration Initialised with ID:", migrationSetId);

      // Immediately submit the migration for export processing
      await handleSubmitMigrationClick(migrationSetId.toString());
      closeModal();
      log("Export is being submitted: ", isSubmitting);
      // Navigate back to main page
      navigate("/embedded/jumpstart");
    } catch (error) {
      console.error("Error during export process:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseImportExportModalComponent
      titleText={titleText}
      subtitleText={subtitleText}
      closeModal={closeModal}
      isLoading={isLoading}
      options={options}
      onToggleOption={handleOptionChange}
      onSelectAll={handleSelectAll}
      onUnselectAll={handleUnselectAll}
      onProceed={handleProceed}
    />
  );
};
